import useSWR from "swr";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import {
  type FetchResponse,
  ProductTypeEnum,
  getCustomizationDetails,
  getProductBlankImages,
  normalizeStoreAlias,
  toast,
} from "shared-components";

import { addProductToShoppingCart } from "@/api/cart";
import { ShoppingCart } from "@/api/cart/models";
import { getProduct, getStoreProductByAlias, getSameStoreProducts, getSimilarProducts } from "@/api/product";
import { addProductToWishlist } from "@/api/wishlist";
import { IWishlist } from "@/api/wishlist/models";
import { useRevalidateShoppingCart } from "@/hooks/useRevalidateShoppingCart";
import { useUserIdentityCookie } from "@/hooks/use-user-identity-cookie";
import { useWishlist } from "./useWishlist";

export const useProductPage = (storeAlias?: string) => {
  const { slug, productId: contextualProductId } = useParams();
  const normalizedStoreAlias = normalizeStoreAlias(storeAlias);
  const productId = contextualProductId ?? slug;
  const { ensureUserIdentityCookie } = useUserIdentityCookie();
  const revalidateShoppingCart = useRevalidateShoppingCart();
  const { getExistingWishlistItemId, removeFromWishlist, mutate: mutateWishlist } = useWishlist(!normalizedStoreAlias);

  const { data, isLoading } = useSWR(
    productId &&
      (normalizedStoreAlias ? ["store/public/product", normalizedStoreAlias, productId] : `/product/${productId}`),
    () =>
      normalizedStoreAlias
        ? getStoreProductByAlias({ alias: normalizedStoreAlias, productId: productId as string })
        : getProduct(productId as string),
  );

  const { data: similarProducts, isLoading: isLoadingSimilarProducts } = useSWR(
    productId && !normalizedStoreAlias ? `/product/similar/${productId}` : null,
    () => getSimilarProducts({ id: productId as string, pagination: { page: 1, total: 10 } }),
  );

  const { data: storeProducts, isLoading: isLoadingStoreProducts } = useSWR(
    productId && `/product/same-store/${productId}`,
    () => (typeof productId === "string" ? getSameStoreProducts(productId) : undefined),
  );

  const [productItemValues, setProductItemValues] = useState({
    blankId: "",
    color: { colorHex: "", colorName: "" },
    size: "",
    quantity: 1,
  });

  useEffect(() => {
    if (data) {
      setProductItemValues({
        ...productItemValues,
        blankId: data.blank?.id || "",
      });
    }
  }, [data]);

  const [loadingAddToCart, setLoadingAddToCart] = useState(false);

  const [loadingAddToWishlist, setLoadingAddToWishlist] = useState(false);

  const [errors, setErrors] = useState({
    statusCode: 0,
    itemValues: {
      blankId: "",
      color: "",
      size: "",
      quantity: "",
    },
  });

  const _cleanErrors = () => {
    setErrors({
      statusCode: 0,
      itemValues: {
        blankId: "",
        color: "",
        size: "",
        quantity: "",
      },
    });
  };

  const _itemPayload = () => ({
    type: ProductTypeEnum.STORE,
    id: data!.id,
    amount: productItemValues.quantity,
    size: productItemValues.size,
    color: productItemValues.color.colorHex,
    ...(normalizedStoreAlias ? { storeAlias: normalizedStoreAlias } : {}),
  });

  const _addItemCallback = <T>(res: FetchResponse<T>, type: "cart" | "wishlist") => {
    if (res.error) {
      toast.error(res.error.message);

      if (res.error.properties) {
        setErrors({
          ...errors,
          ...res.error.properties.reduce(
            (acc, { property, errorMessages }) => ({
              ...acc,
              [property === "amount" ? "quantity" : property]: errorMessages[0],
            }),
            {},
          ),
        });
      }
    } else {
      const message = type === "cart" ? "Produto adicionado ao carrinho!" : 'Produto salvo em "Meus desejos"';
      toast.success(message);
      _cleanErrors();
    }
  };

  const _blankImages = getProductBlankImages(data);

  const customizationDetails = data?.blank
    ? getCustomizationDetails(data, productItemValues.color.colorHex)
    : undefined;
  // Only pass customizationDetails to viewer if it has actual content
  // Empty object would be truthy and cause ImagesVisualizer to use customization logic
  const customizationDetailsForViewer =
    customizationDetails && Object.keys(customizationDetails).length > 0 ? customizationDetails : undefined;

  const imageUrls = data?.blank
    ? _blankImages
        .filter(({ colorHex }) => colorHex === productItemValues.color.colorHex)
        .map(({ imageSrc }) => imageSrc)
    : data?.imageUrls || [];

  const colorOptions =
    _blankImages.reduce(
      (acc, { colorHex, colorName }) => {
        if (acc.every((savedColor) => savedColor.colorHex !== colorHex)) {
          return [...acc, { colorHex, colorName }];
        }

        return acc;
      },
      [] as { colorHex: string; colorName: string }[],
    ) || [];

  const sizes = data?.sizes || [];

  const productInfo = {
    id: data?.id || "",
    name: data?.name || "",
    printName: data?.printName || "",
    store: data?.store,
    price: data?.price || 0,
    themes: data?.themes || [],
    description: data?.description || "",
    technicalSheet: data?.technicalSheet || [],
  };

  useEffect(() => {
    if (data && !productItemValues.color.colorHex && !productItemValues.size) {
      setProductItemValues({
        ...productItemValues,
        blankId: data.blank?.id || "",
        color: {
          colorHex: data.colors?.[0] || "",
          colorName: data.colorNames?.[0] || "",
        },
        size: data.sizes?.[0] || "",
      });
    }
  }, [isLoading]);

  const onAddToCart = async () => {
    setLoadingAddToCart(true);

    try {
      const hasUserIdentityCookie = await ensureUserIdentityCookie();
      if (!hasUserIdentityCookie) {
        return;
      }

      const res = await addProductToShoppingCart(_itemPayload());
      if (!res.error) {
        await revalidateShoppingCart();
      }
      _addItemCallback<ShoppingCart>(res, "cart");
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoadingAddToCart(false);
    }
  };

  const onFavorite = async () => {
    setLoadingAddToWishlist(true);

    const wishlistItemId = getExistingWishlistItemId(
      productId as string,
      productItemValues.color.colorHex,
      productItemValues.size,
    );

    try {
      if (wishlistItemId) {
        await removeFromWishlist(wishlistItemId);
      } else {
        const res = await addProductToWishlist(_itemPayload());
        _addItemCallback<IWishlist>(res, "wishlist");
        await mutateWishlist();
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoadingAddToWishlist(false);
    }
  };

  return {
    productId,
    productInfo,
    colorOptions,
    customizationDetailsForViewer,
    imageUrls,
    blankImages: _blankImages.filter(({ colorHex }) => colorHex === productItemValues.color.colorHex),
    preloadBlankImages: _blankImages,
    sizes,
    productItemValues,
    setProductItemValues,
    isLoading,
    loadingAddToCart,
    loadingAddToWishlist,
    onAddToCart,
    onFavorite,
    isFavorited: !!getExistingWishlistItemId(
      productId as string,
      productItemValues.color.colorHex,
      productItemValues.size,
    ),
    errors,
    similarProducts: similarProducts?.data || [],
    isLoadingSimilarProducts,
    storeProducts: storeProducts?.data || [],
    isLoadingStoreProducts,
  };
};
